/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Alert, Checkbox, Stack } from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';
import { camelCase, debounce, mapKeys } from 'lodash-es';

import Link from 'atoms/Link/Link';
import { FilterAutocomplete } from 'atoms/TableFilter';
import { StyledFilterWrapper } from 'molecules/Filter/Filter.styles';
import InvoicePreview from 'molecules/InvoicePreview';
import Modal from 'molecules/Modal';
import {
  ModalActions,
  ModalCloseButton,
  ModalSubmitButton,
} from 'molecules/Modal/Modal.styles';
import Table from 'molecules/Table';
import {
  StyledCellContainer,
  StyledTableContainer,
} from 'molecules/Table/styles';
import { StyledTablePagination } from 'molecules/Table/styles/Pagination.styles';
import { StyledCellText } from 'molecules/Table/styles/Text.styles';
import PaginationActions from 'molecules/Table/TablePagination/Actions';
import {
  ActionContainerStyled,
  PayButtonStyled,
} from 'organisms/InvoicesTable/InvoicesTable.styles';
import Filter from 'organisms/Filter';
import {
  InvoiceComplexFilterValue,
  getInvoiceFilterOptions,
  mapInvoiceFilters,
} from 'pages/InvoicesPage/invoiceFilters';
import { currencyFormatter } from 'utils/helpers/currencyFormatter';
import { encodeInvoiceCurrencies } from 'utils/helpers/invoiceCurrency';
import InvoiceStatus from 'utils/styles/InvoiceStatus.styles';
import { palette } from 'utils/styles/variables';
import {
  ApprovalInvoiceApi,
  ApprovalInvoiceRow,
} from 'organisms/InvoiceApprovalTable/types';

const APPROVAL_PENDING_STATUS = 'quickbooks_svc_invoice_approval_pending';

const toRow = (invoice: Record<string, unknown>): ApprovalInvoiceRow => {
  const inv = mapKeys(invoice, (v, k) => camelCase(k)) as ApprovalInvoiceApi;
  const dueDays = inv.dueDate
    ? dayjs(inv.dueDate).startOf('day').diff(dayjs().startOf('day'), 'day')
    : null;
  const invoicePeriod =
    inv.invoicePeriod?.Valid && inv.invoicePeriod.Time
      ? dayjs(inv.invoicePeriod.Time.slice(0, 10))
      : null;

  return {
    id: inv.id,
    amount: inv.amount,
    invoiceNumber: inv.invoiceNumber,
    region: inv.qbRegion,
    invoiceType: inv.invoiceType,
    companyName: inv.companyName,
    companyHsId: inv.companyHsId,
    currency: inv.currency,
    totalAmount: inv.totalAmount,
    invoicePeriod:
      invoicePeriod?.isValid() === true ? invoicePeriod.format('MMM YYYY') : '',
    dueDate: inv.dueDate ? dayjs(inv.dueDate).format('MMM DD, YYYY') : '',
    dueDays,
    isApprovalPending: inv.serviceStatus === APPROVAL_PENDING_STATUS,
  };
};

export const InvoiceApprovalTable = () => {
  const [rows, setRows] = useState<ApprovalInvoiceRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);

  const [search, setSearch] = useState('');
  const [currency, setCurrency] = useState('all');
  const [status, setStatus] = useState('all');
  const [dateRange, setDateRange] = useState<[null | Date, null | Date]>([
    null,
    null,
  ]);

  const [confirmIds, setConfirmIds] = useState<string[] | null>(null);
  const [approving, setApproving] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState<{
    invoiceNumber: string;
    region: string;
  } | null>(null);
  const [alertState, setAlertState] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);

  const invoiceFilterOptions = useMemo(
    () =>
      getInvoiceFilterOptions(true).filter(
        ({ code }) => code !== 'invoice_type'
      ),
    []
  );

  const handleFilterChange = useCallback(
    (nextFilterValue: { [key: string]: string[] }) => {
      const mapped = mapInvoiceFilters(
        nextFilterValue as unknown as InvoiceComplexFilterValue
      );

      setDateRange(mapped.dateRange);
      setStatus(mapped.status);
      setCurrency(mapped.currency);
    },
    []
  );

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const selectedCurrencies = encodeInvoiceCurrencies(currency);
      const { data } = await axios.get(
        `${process.env.REACT_APP_REPORT_SERVICE}/invoices/approval-required`,
        {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            ...(search ? { search } : null),
            ...(status !== 'all' ? { statuses: status } : null),
            ...(selectedCurrencies ? { currency: selectedCurrencies } : null),
            ...(dateRange[0] && dateRange[1]
              ? {
                  from: dayjs(dateRange[0]).format('YYYY-MM-DD'),
                  to: dayjs(dateRange[1]).format('YYYY-MM-DD'),
                }
              : null),
          },
        }
      );

      const invoices = (data.invoices || []).map(toRow);
      setRows(invoices);
      setTotal(data.total || 0);

      // e.g. the last invoices of the page were just approved
      if (invoices.length === 0 && data.total > 0 && page > 0) {
        setPage(0);
      }
    } catch (err) {
      setAlertState({
        severity: 'error',
        message: 'Failed to load invoices awaiting approval.',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, status, currency, dateRange]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    setSelected([]);
  }, [page, rowsPerPage, search, status, currency, dateRange]);

  useEffect(() => {
    setPage(0);
  }, [search, status, currency, dateRange]);

  /** debounce handler for two types: textfield and autocomplete */
  const debouncedChangeHandler = useCallback(
    debounce((event, value) => {
      setSearch(value || event?.target?.value || '');
    }, 1000),
    []
  );

  const onReset = useCallback(() => setSearch(''), []);

  const handleSelectRow = useCallback((id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }, []);

  const handleClosePreview = useCallback(() => {
    setInvoicePreview(null);
    document.body.setAttribute('id', '');
  }, []);

  const handleApprove = useCallback(async () => {
    if (!confirmIds || confirmIds.length === 0) return;

    setApproving(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_REPORT_SERVICE}/invoices/approve`,
        { invoice_ids: confirmIds }
      );

      setAlertState({
        severity: 'success',
        message: `${confirmIds.length} ${
          confirmIds.length === 1 ? 'invoice' : 'invoices'
        } approved and queued for creation in the accounting software.`,
      });
      setSelected([]);
      setConfirmIds(null);
      fetchInvoices();
    } catch (err) {
      const apiMessage = axios.isAxiosError(err)
        ? err.response?.data?.message || err.response?.data?.error
        : '';
      setAlertState({
        severity: 'error',
        message: apiMessage || 'Failed to approve invoices. Please try again.',
      });
      setConfirmIds(null);
    } finally {
      setApproving(false);
    }
  }, [confirmIds, fetchInvoices]);

  const approvableIds = useMemo(
    () => rows.filter((row) => row.isApprovalPending).map((row) => row.id),
    [rows]
  );

  const allSelected =
    approvableIds.length > 0 && selected.length === approvableIds.length;
  const someSelected =
    selected.length > 0 && selected.length < approvableIds.length;

  const columns = useMemo(
    () => [
      {
        key: 'checkbox',
        label: (
          <Checkbox
            size="small"
            sx={{ p: 0 }}
            checked={allSelected}
            indeterminate={someSelected}
            disabled={approvableIds.length === 0}
            onChange={(event) =>
              setSelected(event.target.checked ? approvableIds : [])
            }
            inputProps={{
              'aria-label': 'select all invoices awaiting approval',
            }}
          />
        ),
        sortable: false,
        width: '48px',
        render: ({
          id,
          invoiceNumber,
          isApprovalPending,
        }: ApprovalInvoiceRow) =>
          isApprovalPending ? (
            <Checkbox
              size="small"
              sx={{ p: 0 }}
              checked={selected.includes(id)}
              onChange={() => handleSelectRow(id)}
              onClick={(event) => event.stopPropagation()}
              inputProps={{ 'aria-label': `select invoice ${invoiceNumber}` }}
            />
          ) : null,
      },
      {
        key: 'invoicePeriod',
        label: 'Invoice Period',
        sortable: false,
        width: '11%',
        render: ({ invoicePeriod }: ApprovalInvoiceRow) => (
          <StyledCellText
            $textType="bold"
            sx={{ fontSize: '0.813rem !important' }}
          >
            {invoicePeriod}
          </StyledCellText>
        ),
      },
      {
        key: 'invoiceNumber',
        label: 'Invoice #',
        sortable: false,
        width: '12%',
        render: ({ invoiceNumber, region }: ApprovalInvoiceRow) => {
          const canPreview = Boolean(invoiceNumber && region);

          return invoiceNumber ? (
            <StyledCellText
              $color={canPreview ? palette.primary.main : undefined}
              sx={
                canPreview
                  ? {
                      display: 'inline',
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }
                  : undefined
              }
              onClick={
                canPreview
                  ? (event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setInvoicePreview({ invoiceNumber, region });
                      document.body.setAttribute('id', 'body');
                    }
                  : undefined
              }
            >
              #{invoiceNumber}
            </StyledCellText>
          ) : (
            ''
          );
        },
      },
      {
        key: 'companyName',
        label: 'Company',
        sortable: false,
        width: '34%',
        render: ({ companyName, companyHsId }: ApprovalInvoiceRow) => (
          <Link to={`/company/${companyHsId}`} $textDecoration="none">
            <StyledCellText $textType="link">{companyName}</StyledCellText>
          </Link>
        ),
      },
      {
        key: 'amount',
        label: 'Invoice Total',
        sortable: false,
        width: '14%',
        render: ({ amount, currency: rowCurrency }: ApprovalInvoiceRow) => (
          <StyledCellContainer $right $vStack $pr="20px">
            <StyledCellText>
              {currencyFormatter(amount || 0, rowCurrency)}
            </StyledCellText>
          </StyledCellContainer>
        ),
      },
      // {
      //   key: 'invoiceType',
      //   label: 'Type of Invoice',
      //   sortable: false,
      //   width: '10%',
      //   render: ({ invoiceType }: ApprovalInvoiceRow) => (
      //     <StyledCellText>{invoiceType}</StyledCellText>
      //   ),
      // },
      {
        key: 'status',
        label: 'Status',
        greyText: 'Due date',
        sortable: false,
        width: '15%',
        render: ({ dueDays, dueDate }: ApprovalInvoiceRow) => (
          <StyledCellContainer $vStack $display="inline">
            {dueDays !== null && (
              <InvoiceStatus>
                {dueDays > 0 &&
                  `OPEN DUE ${dueDays} ${dueDays === 1 ? 'DAY' : 'DAYS'}`}

                {dueDays === 0 && 'OPEN DUE TODAY'}

                {dueDays < 0 &&
                  `OVERDUE ${Math.abs(dueDays)} ${
                    dueDays === -1 ? 'DAY' : 'DAYS'
                  }`}
              </InvoiceStatus>
            )}

            <StyledCellText $textType="grey">{dueDate}</StyledCellText>
          </StyledCellContainer>
        ),
      },
      {
        key: 'actions',
        label: '',
        sortable: false,
        render: (row: ApprovalInvoiceRow) =>
          row.isApprovalPending ? (
            <ActionContainerStyled>
              <PayButtonStyled
                variant="contained"
                size="small"
                color="secondary"
                disabled={approving}
                onClick={(event) => {
                  event.stopPropagation();
                  setConfirmIds([row.id]);
                }}
              >
                Approve
              </PayButtonStyled>
            </ActionContainerStyled>
          ) : null,
      },
    ],
    [
      approvableIds,
      selected,
      allSelected,
      someSelected,
      approving,
      handleSelectRow,
    ]
  );

  return (
    <>
      <StyledFilterWrapper
        sx={{
          position: 'relative',
          '.filter-popover-container': {
            top: '40px',
            left: '5px',
          },
        }}
      >
        <Stack
          direction="row"
          gap={3}
          flexWrap="wrap"
          py="20px"
          alignItems="center"
        >
          <Filter
            options={invoiceFilterOptions}
            onChange={handleFilterChange}
            loading={loading}
          />

          <FilterAutocomplete
            isFreeSolo={!!search}
            value={search}
            options={[]}
            placeholder="Search by: Company, Invoice #"
            tooltip="Filter by company or invoice #"
            onChange={debouncedChangeHandler}
            onReset={onReset}
          />

          {selected.length > 0 && (
            <PayButtonStyled
              variant="contained"
              color="secondary"
              disabled={approving}
              onClick={() => setConfirmIds(selected)}
            >
              Approve ({selected.length})
            </PayButtonStyled>
          )}
        </Stack>
      </StyledFilterWrapper>

      {alertState && (
        <Alert
          severity={alertState.severity}
          onClose={() => setAlertState(null)}
          sx={{ m: '0 20px 10px' }}
        >
          {alertState.message}
        </Alert>
      )}

      <StyledTableContainer>
        <Table
          idKey="id"
          loading={loading}
          columns={columns}
          data={rows}
          emptyText="No invoices awaiting approval"
        />
        <StyledTablePagination
          labelDisplayedRows={() => null}
          rowsPerPageOptions={[]}
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          ActionsComponent={(props) => (
            <PaginationActions
              {...props}
              labelRowsPerPage="Items per page"
              rowsPerPageOptions={[20, 50, 100]}
              onRowsPerPageChange={(rowsCount) => {
                setRowsPerPage(rowsCount);
                setPage(0);
              }}
            />
          )}
        />
      </StyledTableContainer>

      {invoicePreview && (
        <InvoicePreview
          invoiceNumber={invoicePreview.invoiceNumber}
          region={invoicePreview.region}
          onClose={handleClosePreview}
        />
      )}

      <Modal
        open={Boolean(confirmIds)}
        maxWidth="sm"
        title={`Approve ${
          confirmIds && confirmIds.length === 1 ? 'invoice' : 'invoices'
        }`}
        subtitle={
          <span>
            {`${confirmIds?.length || 0} ${
              confirmIds && confirmIds.length === 1 ? 'invoice' : 'invoices'
            } will be approved and sent to the accounting software.`}
          </span>
        }
        onClose={() => {
          if (!approving) setConfirmIds(null);
        }}
      >
        <ModalActions>
          <ModalCloseButton
            autoFocus
            size="medium"
            variant="outlined"
            disabled={approving}
            onClick={() => setConfirmIds(null)}
          >
            Cancel
          </ModalCloseButton>
          <ModalSubmitButton
            size="medium"
            variant="contained"
            loading={approving}
            onClick={handleApprove}
          >
            Approve
          </ModalSubmitButton>
        </ModalActions>
      </Modal>
    </>
  );
};

export default InvoiceApprovalTable;
